import React, { useState, useEffect } from 'react';
import { Routes, Route, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import logo from '../assets/LogoW.webp';
import AdminPanel from '../pages/AdminPanel';

const API = process.env.REACT_APP_API_URL || 'https://api.pixlybooth.com';

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { socket } = useSocket();
  const navigate = useNavigate();
  const location = useLocation();
  const [pendingCount, setPendingCount] = useState(0);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    import('axios').then(({ default: axios }) => {
      axios.get(`${API}/api/admin/bookings?status=pending`, { withCredentials: true })
        .then(r => setPendingCount(r.data.total || 0)).catch(() => {});
      axios.get(`${API}/api/notifications`, { withCredentials: true })
        .then(r => setUnreadNotifications(r.data.unreadCount || 0)).catch(() => {});
    });
  }, []);

  useEffect(() => {
    if (!socket) return;
    const handle = () => setPendingCount(n => n + 1);
    const handleNotif = () => setUnreadNotifications(n => n + 1);
    socket.on('new_booking', handle);
    socket.on('new_admin_notification', handleNotif);
    return () => {
      socket.off('new_booking', handle);
      socket.off('new_admin_notification', handleNotif);
    };
  }, [socket]);

  const handleLogout = async () => {
    await logout();
    navigate('/admin-login', { replace: true });
  };

  const navGroups = [
    {
      id: 'operations',
      label: 'Bookings & Events',
      icon: '📋',
      badge: pendingCount > 0 ? pendingCount : null,
      badgeColor: '#E34364',
      items: [
        { to: '/admin/bookings', label: 'All Bookings', icon: '📋', badge: pendingCount > 0 ? pendingCount : null, badgeColor: '#E34364' },
        { to: '/admin/organizers', label: 'Organizer History', icon: '🏆' },
        { to: '/admin/contracts', label: 'Contracts', icon: '📄' },
      ],
    },
    {
      id: 'access',
      label: 'Team & Access',
      icon: '👥',
      items: [
        { to: '/admin/roles', label: 'Roles & Permissions', icon: '🛡️' },
        { to: '/admin/customers', label: 'Customers', icon: '👤' },
      ],
    },
    {
      id: 'catalog',
      label: 'Catalog & Pricing',
      icon: '🎪',
      items: [
        { to: '/admin/pricing', label: 'Pricing Manager', icon: '💲' },
        { to: '/admin/booths', label: 'Booths', icon: '🎪' },
        { to: '/admin/packages', label: 'Packages', icon: '📦' },
        { to: '/admin/backdrops', label: 'Backdrops', icon: '🖼' },
      ],
    },
    {
      id: 'finance',
      label: 'Billing & Finance',
      icon: '💳',
      items: [
        { to: '/admin/payments', label: 'Payment History', icon: '💳' },
        { to: '/admin/invoices', label: 'Invoices History', icon: '🧾' },
      ],
    },
    {
      id: 'alerts',
      label: 'System & Alerts',
      icon: '🔔',
      badge: unreadNotifications > 0 ? unreadNotifications : null,
      badgeColor: '#9F5CFF',
      items: [
        { to: '/admin/notifications', label: 'Notifications', icon: '🔔', badge: unreadNotifications > 0 ? unreadNotifications : null, badgeColor: '#9F5CFF' },
      ],
    },
  ];

  const [openGroups, setOpenGroups] = useState({
    operations: true,
    access: false,
    catalog: false,
    finance: false,
    alerts: false,
  });

  // Automatically expand group containing active route on load or change
  useEffect(() => {
    navGroups.forEach(g => {
      const hasActive = g.items.some(item => location.pathname.startsWith(item.to));
      if (hasActive) {
        setOpenGroups(prev => ({ ...prev, [g.id]: true }));
      }
    });
  }, [location.pathname]);

  const toggleGroup = (groupId) => {
    setOpenGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0B0F1F', display: 'flex', fontFamily: 'Arial, Helvetica, sans-serif' }}>
      <style>{`
        @media (min-width: 768px) {
          #admin-sidebar { transform: translateX(0) !important; }
          #admin-content { margin-left: 240px; }
        }
        #admin-sidebar-nav::-webkit-scrollbar {
          width: 4px;
        }
        #admin-sidebar-nav::-webkit-scrollbar-track {
          background: transparent;
        }
        #admin-sidebar-nav::-webkit-scrollbar-thumb {
          background: rgba(159, 92, 255, 0.25);
          border-radius: 4px;
        }
        #admin-sidebar-nav::-webkit-scrollbar-thumb:hover {
          background: rgba(159, 92, 255, 0.45);
        }
      `}</style>

      {/* Sidebar */}
      <div
        id="admin-sidebar"
        style={{
          width: 240,
          background: 'linear-gradient(180deg, #101428 0%, #0D0F20 100%)',
          borderRight: '1px solid rgba(159,92,255,0.2)',
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          maxHeight: '100vh',
          overflow: 'hidden',
          position: 'fixed',
          top: 0,
          left: 0,
          zIndex: 100,
          transform: mobileOpen ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {/* Header (fixed) */}
        <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0 }}>
          <img src={logo} alt="PixlyBooth" style={{ height: 38 }} />
          <span style={{ display: 'block', fontSize: 10, color: '#9F5CFF', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginTop: 4 }}>
            Admin Panel
          </span>
        </div>

        {/* User Card (fixed) */}
        <div style={{ padding: '12px 18px', borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg, #00D2FF 0%, #9F5CFF 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
            {user?.name?.[0]?.toUpperCase()}
          </div>
          <div style={{ overflow: 'hidden' }}>
            <p style={{ margin: 0, fontSize: 12, color: '#fff', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.name}</p>
            <span style={{ fontSize: 10, color: '#00D2FF', textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.5 }}>Administrator</span>
          </div>
        </div>

        {/* Navigation Body (scrollable) */}
        <nav
          id="admin-sidebar-nav"
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '12px 10px',
            overscrollBehavior: 'contain',
          }}
        >
          {/* Standalone Dashboard Link */}
          <NavLink
            to="/admin"
            end
            onClick={() => setMobileOpen(false)}
            style={({ isActive }) => ({
              display: 'flex',
              alignItems: 'center',
              gap: 9,
              padding: '9px 12px',
              borderRadius: 8,
              marginBottom: 8,
              fontSize: 12.5,
              textDecoration: 'none',
              fontWeight: isActive ? 700 : 500,
              color: isActive ? '#fff' : '#a0a0b8',
              background: isActive ? 'rgba(159,92,255,0.18)' : 'rgba(255,255,255,0.02)',
              border: isActive ? '1px solid rgba(159,92,255,0.4)' : '1px solid transparent',
              borderLeft: isActive ? '3px solid #9F5CFF' : '1px solid transparent',
              transition: 'all 0.15s',
            })}
          >
            <span style={{ fontSize: 14 }}>📊</span>
            <span>Analytics Overview</span>
          </NavLink>

          {/* Group Dropdowns */}
          {navGroups.map((group) => {
            const isOpen = Boolean(openGroups[group.id]);
            const hasActiveChild = group.items.some(item => location.pathname.startsWith(item.to));

            return (
              <div key={group.id} style={{ marginBottom: 4 }}>
                {/* Group Accordion Header */}
                <button
                  type="button"
                  onClick={() => toggleGroup(group.id)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 10px',
                    borderRadius: 8,
                    background: hasActiveChild && !isOpen ? 'rgba(159,92,255,0.08)' : (isOpen ? 'rgba(255,255,255,0.03)' : 'transparent'),
                    border: 'none',
                    color: hasActiveChild ? '#fff' : '#8888aa',
                    cursor: 'pointer',
                    fontSize: 12,
                    fontWeight: 600,
                    textAlign: 'left',
                    transition: 'all 0.15s ease',
                  }}
                  onMouseEnter={e => {
                    if (!isOpen && !hasActiveChild) e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
                  }}
                  onMouseLeave={e => {
                    if (!isOpen && !hasActiveChild) e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, overflow: 'hidden' }}>
                    <span style={{ fontSize: 13 }}>{group.icon}</span>
                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {group.label}
                    </span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                    {group.badge && (
                      <span
                        style={{
                          background: group.badgeColor || '#E34364',
                          color: '#fff',
                          fontSize: 9.5,
                          fontWeight: 700,
                          padding: '1px 6px',
                          borderRadius: 50,
                          lineHeight: '14px',
                        }}
                      >
                        {group.badge}
                      </span>
                    )}
                    <span
                      style={{
                        fontSize: 10,
                        color: hasActiveChild ? '#9F5CFF' : '#666',
                        display: 'inline-block',
                        transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                    >
                      ▸
                    </span>
                  </div>
                </button>

                {/* Group Dropdown Children */}
                {isOpen && (
                  <div
                    style={{
                      paddingLeft: 8,
                      marginLeft: 14,
                      borderLeft: '1px solid rgba(159,92,255,0.2)',
                      marginTop: 2,
                      marginBottom: 4,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                    }}
                  >
                    {group.items.map((item) => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        onClick={() => setMobileOpen(false)}
                        style={({ isActive }) => ({
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '7px 10px',
                          borderRadius: 6,
                          fontSize: 12,
                          textDecoration: 'none',
                          fontWeight: isActive ? 700 : 400,
                          color: isActive ? '#fff' : '#a0a0b8',
                          background: isActive ? 'rgba(159,92,255,0.18)' : 'transparent',
                          borderLeft: isActive ? '2px solid #9F5CFF' : '2px solid transparent',
                          transition: 'all 0.12s',
                        })}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7, overflow: 'hidden' }}>
                          <span style={{ fontSize: 12 }}>{item.icon}</span>
                          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {item.label}
                          </span>
                        </div>

                        {item.badge && (
                          <span
                            style={{
                              background: item.badgeColor || '#E34364',
                              color: '#fff',
                              fontSize: 9,
                              fontWeight: 700,
                              padding: '1px 5px',
                              borderRadius: 50,
                              marginLeft: 4,
                            }}
                          >
                            {item.badge}
                          </span>
                        )}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Footer (fixed) */}
        <div style={{ padding: '12px 14px', borderTop: '1px solid rgba(255,255,255,0.06)', flexShrink: 0, background: 'rgba(0,0,0,0.15)' }}>
          <button
            onClick={handleLogout}
            style={{
              width: '100%',
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 8,
              padding: '8px 12px',
              color: '#a0a0b8',
              fontSize: 12,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontWeight: 600,
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.color = '#fff'; e.currentTarget.style.borderColor = 'rgba(227,67,100,0.4)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#a0a0b8'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}
          >
            <span>🚪</span>
            <span>Sign Out</span>
          </button>
          <NavLink
            to="/"
            style={{
              display: 'block',
              padding: '6px 10px',
              fontSize: 11,
              color: '#555',
              textDecoration: 'none',
              textAlign: 'center',
              marginTop: 4,
              transition: 'color 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.color = '#9F5CFF'}
            onMouseLeave={e => e.currentTarget.style.color = '#555'}
          >
            ← Homepage
          </NavLink>
        </div>
      </div>

      {mobileOpen && <div onClick={() => setMobileOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 99 }} />}

      {/* Content */}
      <div id="admin-content" style={{ flex: 1, padding: '24px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, paddingBottom: 16, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <button onClick={() => setMobileOpen(true)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer' }}>☰</button>
          <h1 style={{ margin: 0, fontSize: 18, color: '#fff', fontWeight: 700 }}>Admin Panel</h1>
          {pendingCount > 0 && (
            <span style={{ marginLeft: 'auto', background: '#E34364', color: '#fff', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 50, animation: 'pulse 2s infinite' }}>
              {pendingCount} pending
            </span>
          )}
        </div>

        <Routes>
          <Route index element={<AdminPanel tab="analytics" />} />
          <Route path="bookings" element={<AdminPanel tab="bookings" setPendingCount={setPendingCount} />} />
          <Route path="customers" element={<AdminPanel tab="customers" />} />
          <Route path="pricing" element={<AdminPanel tab="pricing" />} />
          <Route path="booths" element={<AdminPanel tab="booths" />} />
          <Route path="packages" element={<AdminPanel tab="packages" />} />
          <Route path="backdrops" element={<AdminPanel tab="backdrops" />} />
          <Route path="contracts" element={<AdminPanel tab="contracts" />} />
          <Route path="payments" element={<AdminPanel tab="payments" />} />
          <Route path="invoices" element={<AdminPanel tab="invoices" />} />
          <Route path="organizers" element={<AdminPanel tab="organizers" />} />
          <Route path="roles" element={<AdminPanel tab="roles" />} />
          <Route path="notifications" element={<AdminPanel tab="notifications" setUnreadNotifications={setUnreadNotifications} />} />
        </Routes>
      </div>
    </div>
  );
}
